import React from 'react';
import { useTranslation } from 'react-i18next';

const Footer = ({ setActiveSection }) => {
  const currentYear = new Date().getFullYear();
  const { t } = useTranslation();

  const navItems = [
    { id: 'home', label: t('nav.home') },
    { id: 'gallery', label: t('nav.gallery') },
    { id: 'about', label: t('nav.about') },
    { id: 'exhibitions', label: t('nav.exhibitions') },
    { id: 'contacts', label: t('nav.contacts') },
  ];

  return (
    <footer className="bg-[#2A2520] text-[#FAF7F2] safe-area-inset-bottom">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 xs:px-6 lg:px-8 py-10 xs:py-12 sm:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 xs:gap-10 sm:gap-12">
          {/* Brand */}
          <div className="lg:col-span-2">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 rounded-full overflow-hidden border border-[#B8963E]/50">
                <img
                  src="/pics/ivp_logo.png"
                  alt={t('footer.artistName')}
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <h3 className="text-xl font-normal tracking-wide">
                  {t('footer.artistName')}
                </h3>
                <p className="text-xs text-[#C4B9A8] tracking-widest uppercase">
                  {t('footer.artistTitle')}
                </p>
              </div>
            </div>
            <p className="text-[#C4B9A8] leading-relaxed max-w-md mb-6">
              {t('footer.description')}
            </p>

            {/* Social Links - touch-friendly 44x44px */}
            <div className="flex space-x-3 xs:space-x-4">
              {['instagram', 'telegram', 'vk'].map((social) => (
                <button
                  key={social}
                  type="button"
                  className="w-11 h-11 xs:w-10 xs:h-10 rounded-full border border-[#5A5249] flex items-center justify-center text-[#C4B9A8] hover:border-[#B8963E] hover:text-[#B8963E] transition-all duration-300"
                  aria-label={social}
                  style={{ minWidth: '44px', minHeight: '44px' }}
                >
                  {social === 'instagram' && (
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                    </svg>
                  )}
                  {social === 'telegram' && (
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                    </svg>
                  )}
                  {social === 'vk' && (
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M15.684 0H8.316C1.592 0 0 1.592 0 8.316v7.368C0 22.408 1.592 24 8.316 24h7.368C22.408 24 24 22.408 24 15.684V8.316C24 1.592 22.408 0 15.684 0zm3.692 17.123h-1.744c-.66 0-.864-.525-2.05-1.727-1.033-1-1.49-1.135-1.744-1.135-.356 0-.458.102-.458.593v1.575c0 .424-.135.678-1.253.678-1.846 0-3.896-1.118-5.335-3.202C4.624 10.857 4 8.684 4 8.256c0-.254.102-.491.593-.491h1.744c.44 0 .61.203.78.677.863 2.49 2.303 4.675 2.896 4.675.22 0 .322-.102.322-.66V9.721c-.068-1.186-.695-1.287-.695-1.71 0-.203.17-.407.44-.407h2.744c.373 0 .508.203.508.643v3.473c0 .372.17.508.271.508.22 0 .407-.136.813-.542 1.254-1.406 2.151-3.574 2.151-3.574.119-.254.322-.491.763-.491h1.744c.525 0 .644.27.525.643-.22 1.017-2.354 4.031-2.354 4.031-.186.305-.254.44 0 .78.186.254.796.779 1.203 1.253.745.847 1.32 1.558 1.473 2.05.17.49-.085.744-.576.744z"/>
                    </svg>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Navigation */}
          <div>
            <h4 className="text-xs xs:text-sm text-[#B8963E] tracking-widest uppercase mb-4 xs:mb-6">
              {t('footer.sections')}
            </h4>
            <nav className="space-y-1 xs:space-y-2">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => {
                    setActiveSection(item.id);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className="block py-2 text-[#C4B9A8] hover:text-[#FAF7F2] transition-colors duration-300"
                  style={{ minHeight: '44px' }}
                >
                  {item.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-xs xs:text-sm text-[#B8963E] tracking-widest uppercase mb-4 xs:mb-6">
              {t('footer.contacts')}
            </h4>
            <div className="space-y-3 xs:space-y-4">
              <div>
                <p className="text-xs text-[#8B7E6A] mb-1">{t('footer.email')}</p>
                <a
                  href="mailto:info@irina-podlevskikh.ru"
                  className="text-[#C4B9A8] hover:text-[#FAF7F2] transition-colors inline-block py-1"
                  style={{ minHeight: '44px' }}
                >
                  info@irina-podlevskikh.ru
                </a>
              </div>
              <div>
                <p className="text-xs text-[#8B7E6A] mb-1">{t('footer.phone')}</p>
                <a
                  href="tel:+79001234567"
                  className="text-[#C4B9A8] hover:text-[#FAF7F2] transition-colors inline-block py-1"
                  style={{ minHeight: '44px' }}
                >
                  +7 (900) 123-45-67
                </a>
              </div>
              <div>
                <p className="text-xs text-[#8B7E6A] mb-1">{t('footer.studio')}</p>
                <p className="text-[#C4B9A8]">
                  {t('footer.studioAddress')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-[#3D3831]">
        <div className="max-w-7xl mx-auto px-4 xs:px-6 lg:px-8 py-4 xs:py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-3 xs:space-y-4 md:space-y-0 text-center md:text-left">
            <p className="text-xs xs:text-sm text-[#8B7E6A]">
              © {currentYear} {t('footer.copyright')}
            </p>
            <p className="text-xs text-[#5A5249]">
              {t('footer.tagline')}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
